'use client'

import { useState } from 'react'
import type { IWorkspaceWithSubscription } from '@/hooks/subscriptions/use-workspaces'
import { Building2, ChevronDown } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { WorkspaceSelectorModal } from './workspace-selector-modal'

interface WorkspaceSelectorProps {
  workspaces: IWorkspaceWithSubscription[]
  selectedWorkspaceId: string | null
  onWorkspaceChange: (workspaceId: string) => void
}

/**
 * Workspace selector for pricing page
 * Opens a modal (desktop) or drawer (mobile) to select workspace
 */
export function WorkspaceSelector({
  workspaces,
  selectedWorkspaceId,
  onWorkspaceChange,
}: WorkspaceSelectorProps) {
  const t = useTranslations('pricing-v3')
  const [open, setOpen] = useState(false)

  // Sort workspaces by latest updated (most recent first)
  const sortedWorkspaces = [...workspaces].sort((a, b) => {
    const timeA = new Date(a.update_time).getTime()
    const timeB = new Date(b.update_time).getTime()
    return timeB - timeA
  })

  // Get selected workspace details
  const selectedWorkspace = sortedWorkspaces.find(
    (w) => w._id.toString() === selectedWorkspaceId,
  )

  return (
    <>
      {/* Workspace Selector - Compact version next to title */}
      <Button
        variant="outline"
        onClick={() => {
          return setOpen(true)
        }}
        className="h-8 px-2.5 bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20 text-white text-xs"
      >
        <Building2 className="size-3 text-white/60 shrink-0 mr-1.5" />
        <span className="text-xs text-white/70 truncate max-w-[100px]">
          {selectedWorkspaceId === 'new'
            ? t('new_workspace')
            : selectedWorkspace
              ? selectedWorkspace.name ||
                `WS ${selectedWorkspace._id.toString().slice(-6)}`
              : t('workspace_selector.placeholder')}
        </span>
        <ChevronDown className="size-3 text-white/60 shrink-0 ml-1.5" />
      </Button>

      {/* Workspace Selector Modal/Drawer */}
      <WorkspaceSelectorModal
        open={open}
        onOpenChange={setOpen}
        workspaces={sortedWorkspaces}
        selectedWorkspaceId={selectedWorkspaceId}
        onWorkspaceChange={onWorkspaceChange}
      />
    </>
  )
}

/**
 * Workspace subscription details component (compact version for top right)
 * This shows the subscription details that were originally in the workspace selector
 */
export function WorkspaceSubscriptionDetails({
  workspace,
}: {
  workspace: IWorkspaceWithSubscription | null
}) {
  const t = useTranslations('pricing-v3')

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  if (!workspace) return null

  // Subscription Details Card
  if (workspace.subscription?.status === 'active') {
    return (
      <div className="p-4 bg-white/5 border border-white/10 rounded-lg text-xs">
        <h3 className="text-xs font-semibold text-white mb-3">
          Subscription Details
        </h3>
        <div className="space-y-2">
          {/* Credits */}
          <div className="flex justify-between">
            <p className="text-white/60">{t('credits_per_month')}</p>
            <p className="font-semibold text-white">
              {workspace.subscription.credits_per_month?.toLocaleString() || 0}
            </p>
          </div>

          {/* Domains */}
          <div className="flex justify-between">
            <p className="text-white/60">{t('custom_domains')}</p>
            <p className="font-semibold text-white">
              {workspace.subscription.limits?.max_domains === 999
                ? t('unlimited')
                : (workspace.subscription.limits?.max_domains ?? 0)}
            </p>
          </div>

          {/* Next Renewal */}
          <div className="flex justify-between">
            <p className="text-white/60">{t('next_renewal')}</p>
            <p className="font-semibold text-white">
              {formatDate(workspace.subscription.next_renewal_at / 1000)}
            </p>
          </div>

          {/* Billing Cycle */}
          <div className="pt-2 mt-2 border-t border-white/10 flex justify-between">
            <p className="text-white/60">{t('billing_cycle')}</p>
            <p className="font-medium text-white capitalize">
              {workspace.subscription.billing_cycle}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return null
}
